import React, { useRef, useState } from 'react';
import { Calendar, MapPin, Phone, Mail, Download, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { Button } from '../ui/button';
import JamboreeQR from './JamboreeQR';
import { useUpcomingEvent } from '../../hooks/useUpcomingEvent';

// Dynamic, code-driven Panthers Classic (football + flag) flyer.
// Mirrors the printed flyer's dramatic dark/gold aesthetic and stays in sync
// with mock.js data (teams list, tagline, coach contacts).

const WRAPPER_STYLE = {
  maxWidth: '760px',
  margin: '0 auto',
  borderRadius: '20px',
  overflow: 'hidden',
  position: 'relative',
  background:
    'radial-gradient(circle at 15% 5%, rgba(255, 215, 0, 0.28) 0%, transparent 42%), ' +
    'radial-gradient(circle at 85% 95%, rgba(255, 215, 0, 0.18) 0%, transparent 42%), ' +
    'linear-gradient(155deg, #050505 0%, #100a00 55%, #050505 100%)',
  border: '2px solid rgba(255, 215, 0, 0.55)',
  boxShadow: '0 30px 90px rgba(255, 215, 0, 0.25)',
  padding: 'clamp(1.5rem, 4vw, 2.5rem)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'clamp(0.9rem, 2vw, 1.35rem)',
  color: '#fff',
};

const KICKER_STYLE = {
  color: '#FFD700',
  fontSize: '0.72rem',
  letterSpacing: '3.5px',
  fontWeight: 800,
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: '-0.35rem',
};

const TITLE_STYLE = {
  fontSize: 'clamp(2.75rem, 8vw, 4.5rem)',
  fontWeight: 900,
  letterSpacing: '3px',
  color: '#FFD700',
  lineHeight: 0.95,
  margin: 0,
  textAlign: 'center',
  textShadow: '0 4px 32px rgba(255, 215, 0, 0.45), 0 1px 0 rgba(0,0,0,0.4)',
  fontStyle: 'italic',
  transform: 'skewX(-4deg)',
};

const SUBTITLE_STYLE = {
  fontSize: 'clamp(0.85rem, 2.1vw, 1.05rem)',
  letterSpacing: '4px',
  fontWeight: 700,
  color: '#fff',
  margin: 0,
  textTransform: 'uppercase',
  textAlign: 'center',
};

const FEATURES_ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'clamp(0.4rem, 1.2vw, 0.7rem)',
};

const FEATURE_STYLE = {
  background: 'linear-gradient(145deg, rgba(255,215,0,0.16) 0%, rgba(255,215,0,0.04) 100%)',
  border: '1px solid rgba(255, 215, 0, 0.4)',
  borderRadius: '12px',
  padding: '0.75rem 0.55rem',
  textAlign: 'center',
};

const TEAMS_BOX_STYLE = {
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid rgba(255, 215, 0, 0.35)',
  borderRadius: '12px',
  padding: 'clamp(0.85rem, 2vw, 1.1rem)',
};

const TEAMS_HEADER_STYLE = {
  textAlign: 'center',
  color: '#FFD700',
  fontSize: '0.72rem',
  letterSpacing: '3px',
  fontWeight: 800,
  textTransform: 'uppercase',
  marginBottom: '0.7rem',
};

const TEAMS_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '0.4rem',
};

const TEAM_CHIP_STYLE = {
  background: 'rgba(255, 215, 0, 0.1)',
  border: '1px solid rgba(255, 215, 0, 0.4)',
  borderRadius: '999px',
  padding: '0.4rem 0.75rem',
  color: '#fff',
  fontSize: 'clamp(0.72rem, 1.6vw, 0.85rem)',
  fontWeight: 600,
  textAlign: 'center',
};

const DATE_ROW_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '0.5rem 1.5rem',
  fontSize: 'clamp(0.85rem, 2vw, 1rem)',
  padding: '0.75rem 0.5rem',
  background: 'rgba(255, 215, 0, 0.06)',
  border: '1px solid rgba(255, 215, 0, 0.28)',
  borderRadius: '10px',
};

const CONTACTS_ROW_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-around',
  alignItems: 'center',
  gap: '0.75rem 1rem',
};

const CONTACT_ITEM_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  color: '#fff',
  fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)',
  fontWeight: 600,
};

const TAGLINE_BANNER_STYLE = {
  background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 50%, #FFD700 100%)',
  color: '#000',
  padding: '0.6rem 1rem',
  borderRadius: '10px',
  fontWeight: 900,
  fontSize: 'clamp(0.72rem, 1.8vw, 0.9rem)',
  letterSpacing: '2.5px',
  textAlign: 'center',
  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.35)',
};

const PanthersClassicFlyer = () => {
  const { event: upcomingEvent } = useUpcomingEvent();
  const flyerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!flyerRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(flyerRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#050505',
      });
      const link = document.createElement('a');
      link.download = 'Randallstown-Panthers-Classic-2026.png';
      link.href = dataUrl;
      link.click();
    } catch {
      /* html-to-image can fail on cross-origin webfonts — silent */
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div ref={flyerRef} data-testid="panthers-classic-flyer" style={WRAPPER_STYLE}>
        {/* HEADER */}
        <div>
          <div style={KICKER_STYLE}>Randallstown Panthers Present</div>
          <h1 style={TITLE_STYLE}>CLASSIC</h1>
          <p style={SUBTITLE_STYLE}>{upcomingEvent.subtitle}</p>
        </div>

        {/* FEATURE BADGES */}
        <div style={FEATURES_ROW_STYLE} data-testid="panthers-classic-features">
          {upcomingEvent.features.map((f) => (
            <div key={f.title} style={FEATURE_STYLE}>
              <div style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2rem)', marginBottom: '0.15rem' }} aria-hidden>
                {f.emoji}
              </div>
              <div style={{ color: '#FFD700', fontWeight: 800, fontSize: 'clamp(0.7rem, 1.55vw, 0.82rem)', letterSpacing: '0.5px', lineHeight: 1.15 }}>
                {f.title}
              </div>
              <div style={{ color: '#d4d4d4', fontSize: 'clamp(0.62rem, 1.4vw, 0.72rem)', marginTop: '0.2rem' }}>
                {f.subtitle}
              </div>
            </div>
          ))}
        </div>

        {/* DATE / LOCATION */}
        <div style={DATE_ROW_STYLE}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <Calendar size={16} style={{ color: '#FFD700' }} />
            <strong>{upcomingEvent.date}</strong>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <MapPin size={16} style={{ color: '#FFD700' }} />
            <span>{upcomingEvent.venue} · {upcomingEvent.city}</span>
          </span>
        </div>

        {/* REPRESENTED TEAMS */}
        <div style={TEAMS_BOX_STYLE}>
          <div style={TEAMS_HEADER_STYLE}>Represented Teams</div>
          <div style={TEAMS_GRID_STYLE}>
            {upcomingEvent.teamsCommitted.map((team) => (
              <div key={team} style={TEAM_CHIP_STYLE}>{team}</div>
            ))}
          </div>
        </div>

        {/* COACHES + QR */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ flex: '1 1 260px' }}>
            <div style={{
              ...TEAMS_HEADER_STYLE,
              marginBottom: '0.55rem',
            }}>Contact Coaches to Register</div>
            <div style={CONTACTS_ROW_STYLE}>
              {upcomingEvent.contacts.map((c) => (
                <span key={c.phone} style={CONTACT_ITEM_STYLE}>
                  <Phone size={14} style={{ color: '#FFD700' }} />
                  <strong>{c.name}</strong>&nbsp;·&nbsp;{c.phone}
                </span>
              ))}
            </div>
            {upcomingEvent.email && (
              <div style={{ ...CONTACT_ITEM_STYLE, color: '#FFD700', justifyContent: 'center', marginTop: '0.5rem', display: 'flex' }}>
                <Mail size={13} />
                {upcomingEvent.email}
              </div>
            )}
          </div>
          <JamboreeQR size={88} caption="SCAN · REGISTER" />
        </div>

        {/* TAGLINE BANNER */}
        <div style={TAGLINE_BANNER_STYLE}>{upcomingEvent.tagline}</div>
      </div>

      {/* Download button (outside captured area) */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <Button
          className="btn-primary"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-panthers-classic-flyer-btn"
          style={{ fontWeight: 700 }}
        >
          {downloading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              &nbsp;Preparing PNG...
            </>
          ) : (
            <>
              <Download size={18} />
              &nbsp;Download Flyer as PNG
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PanthersClassicFlyer;
